import React, { useState, useRef } from 'react';
import { Plus } from 'lucide-react';
import GroupFilterChip from 'features/activity/components/GroupFilterChip';
import GroupWhoFilterChip from 'features/activity/components/GroupWhoFilterChip';
import GroupCohortChip from 'features/activity/components/GroupCohortChip';
import GroupPropertyFilterPopup from 'features/activity/components/GroupPropertyFilterPopup';

const isGroupProp = (f) => (f.propertySource || f.category) === 'group';
const isEventFilter = (f) => (f.propertySource || f.category) === 'event';
const isCohortFilter = (f) => (f.propertySource || f.category) === 'cohort';

const isWhereFilter = (f) => isGroupProp(f) || isCohortFilter(f);

/**
 * Groups filter module:
 * - WHERE block: group properties + cohorts (same connector rules)
 * - WHO block: event filters (groups whose members did an event)
 */
const GroupsFilterModule = ({ filters = [], onFiltersChange, teamId, groupKey }) => {
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null);
  const addButtonRef = useRef(null);

  const whereFilters = (filters || []).filter(isWhereFilter);
  const eventFilters = (filters || []).filter(isEventFilter);

  const handlePropertyAdd = (newFilter) => {
    const isEvt = isEventFilter(newFilter);
    const targetList = isEvt ? eventFilters : whereFilters;
    const firstConnector = isEvt ? 'WHO' : 'WHERE';

    const connector =
      targetList.length === 0
        ? firstConnector
        : filters.some((f) => f.id === newFilter.id)
          ? filters.find((f) => f.id === newFilter.id)?.connector || 'AND'
          : 'AND';

    const withConnector = { ...newFilter, connector };

    if (filters.some((f) => f.id === newFilter.id)) {
      onFiltersChange(filters.map((f) => (f.id === newFilter.id ? withConnector : f)));
    } else {
      onFiltersChange([...filters, withConnector]);
    }
    setIsAddPopupOpen(false);
    setEditingFilter(null);
  };

  const handleFilterUpdate = (filterId, updates) => {
    onFiltersChange(filters.map((f) => (f.id === filterId ? { ...f, ...updates } : f)));
  };

  const handleFilterRemove = (filterId) => {
    const remaining = filters.filter((f) => f.id !== filterId);
    const whereRemaining = remaining.filter(isWhereFilter);
    const evtRemaining = remaining.filter(isEventFilter);
    const withConnectors = remaining.map((f) => {
      if (isEventFilter(f)) {
        const idx = evtRemaining.findIndex((x) => x.id === f.id);
        const connector = idx === 0 ? 'WHO' : f.connector === 'OR' ? 'OR' : 'AND';
        return { ...f, connector };
      }
      const idx = whereRemaining.findIndex((x) => x.id === f.id);
      const connector = idx === 0 ? 'WHERE' : f.connector === 'OR' ? 'OR' : 'AND';
      return { ...f, connector };
    });
    onFiltersChange(withConnectors);
    if (editingFilter?.id === filterId) {
      setEditingFilter(null);
      setIsAddPopupOpen(false);
    }
  };

  const handleEditFilter = (filter) => {
    setEditingFilter(filter);
    setIsAddPopupOpen(true);
  };

  const hasFilters = filters.length > 0;

  return (
    <div className="bg-transparent border border-dotted border-[color:var(--border-default)] rounded-lg overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="bg-transparent overflow-hidden">
        <div className="pl-4 pr-6 pt-2 pb-3 flex items-center justify-between border-b border-dotted border-[color:var(--border-default)]">
          <h3 className="text-xs font-bold uppercase tracking-wide text-[color:var(--text-primary)]">FILTERS</h3>
        </div>
        <div className="p-3 sm:p-6">
          {whereFilters.length > 0 && (
            <div className="space-y-2 mb-4">
              {whereFilters.map((filter, index) =>
                isCohortFilter(filter) ? (
                  <GroupCohortChip
                    key={filter.id}
                    filter={filter}
                    isFirst={index === 0}
                    onEdit={() => handleEditFilter(filter)}
                    onUpdate={(updates) => handleFilterUpdate(filter.id, updates)}
                    onRemove={() => handleFilterRemove(filter.id)}
                  />
                ) : (
                  <GroupFilterChip
                    key={filter.id}
                    filter={{
                      ...filter,
                      propertySource: 'group',
                      property: filter.property || filter.field,
                      propertyLabel: filter.propertyLabel,
                      propertyType: filter.propertyType,
                    }}
                    isFirst={index === 0}
                    teamId={teamId}
                    groupKey={groupKey}
                    onEdit={() => handleEditFilter(filter)}
                    onUpdate={(updates) => handleFilterUpdate(filter.id, updates)}
                    onRemove={() => handleFilterRemove(filter.id)}
                  />
                )
              )}
            </div>
          )}

          {eventFilters.length > 0 && (
            <div className="space-y-2 mb-4">
              {eventFilters.map((filter, index) => (
                <GroupWhoFilterChip
                  key={filter.id}
                  filter={{
                    ...filter,
                    propertySource: 'event',
                    property: filter.property || filter.field,
                    propertyLabel: filter.propertyLabel,
                    propertyType: filter.propertyType,
                    didOperator: filter.didOperator || 'did',
                  }}
                  isFirst={index === 0}
                  teamId={teamId}
                  onEdit={() => handleEditFilter(filter)}
                  onUpdate={(updates) => handleFilterUpdate(filter.id, updates)}
                  onRemove={() => handleFilterRemove(filter.id)}
                />
              ))}
            </div>
          )}

          <div>
            <button
              ref={addButtonRef}
              type="button"
              onClick={() => {
                setEditingFilter(null);
                setIsAddPopupOpen(true);
              }}
              className="flex items-center gap-2 text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>
      </div>

      {hasFilters && (
        <div className="px-6 pb-2">
          <button
            type="button"
            onClick={() => onFiltersChange([])}
            className="text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
          >
            Clear all
          </button>
        </div>
      )}

      {(isAddPopupOpen || editingFilter) && (
        <GroupPropertyFilterPopup
          isOpen={isAddPopupOpen || !!editingFilter}
          onClose={() => {
            setIsAddPopupOpen(false);
            setEditingFilter(null);
          }}
          triggerRef={addButtonRef}
          teamId={teamId}
          groupKey={groupKey}
          existingFilter={editingFilter}
          onAdd={handlePropertyAdd}
        />
      )}
    </div>
  );
};

export default GroupsFilterModule;
